import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DemocracyWorksElections from "../../components/UpcomingElectionsComponents/DemocracyWorksElections";
import PublicElectionAddressForm from "./PublicElectionAddressForm";
import { US_STATES } from "../../utils/usStates";
import { fetchPublicDWElections } from "../../utils/democracyWorksApi";
import { theme } from "../../theme/colors";

const SITE_URL = "https://civixthread.com";
const APP_STORE_URL = "https://apps.apple.com/us/app/civixthread/id6756985264";

const PRIMARY = "#047857";
const PRIMARY_DARK = "#065f46";
const CTA_GOLD = theme.foundingBadgeBorder;
const CTA_GOLD_TEXT = theme.foundingBadgeText;
const CTA_GOLD_BG = "rgba(212, 160, 23, 0.14)";
const CTA_GOLD_BG_HOVER = "rgba(212, 160, 23, 0.24)";
const TEXT_PRIMARY = "#000000";
const TEXT_SECONDARY = "#000000";

// LLM-quotable national FAQ. First sentence under each H2 is a complete,
// standalone answer (no preamble) so generative engines can lift it directly.
const NATIONAL_FAQ = [
  {
    q: "When is the 2026 general election?",
    a: "The 2026 U.S. general election is on Tuesday, November 3, 2026. All 435 U.S. House seats and about one-third of U.S. Senate seats are up, along with dozens of governorships and thousands of state and local offices.",
  },
  {
    q: "When are the 2026 primary elections?",
    a: "Most state primaries for the 2026 election cycle take place between March and September 2026. Each state sets its own primary date. Enter your address or choose your state above to see your specific dates.",
  },
  {
    q: "What is the difference between a primary and a general election?",
    a: "A primary election decides which candidate will represent each political party in the general election. A general election decides who actually wins the office, with candidates from every party (and any independents) on the same ballot.",
  },
  {
    q: "How do I register to vote?",
    a: "You can register to vote online, by mail, or in person in most U.S. states. Registration rules and deadlines vary by state. Pick your state below to see your exact options and deadlines.",
  },
  {
    q: "How do I find my polling place?",
    a: "Your polling place is determined by your registered address. Most states publish an official polling place lookup; CivixThread links to the official lookup for your state on each state page.",
  },
  {
    q: "What is a ballot measure?",
    a: "A ballot measure is a question put directly to voters, for example a constitutional amendment, a bond, or a citizen initiative, that becomes law if approved. Ballot measures appear alongside candidate races on your ballot.",
  },
  {
    q: "Who can vote in U.S. elections?",
    a: "You can vote in U.S. federal elections if you are a U.S. citizen, at least 18 years old by Election Day, a resident of the state where you're registering, and not disqualified by your state's rules. State and local rules vary on residency, mental capacity, and felony conviction status.",
  },
  {
    q: "How do I vote by mail (absentee)?",
    a: "Most states let any registered voter request a mail or absentee ballot, while a few still require an excuse. Request the ballot before your state's deadline, fill it out at home, and return it by mail or to an official drop box by the return deadline. Choose your state below for exact rules and deadlines.",
  },
];

const buildJsonLd = () => {
  const today = new Date().toISOString();
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/elections`,
      url: `${SITE_URL}/elections`,
      name: "2026 Elections & Ballot Measures by State",
      description:
        "Find 2026 elections by state or address. See voter registration deadlines, ballot measures, candidates, and voting guidance.",
      inLanguage: "en-US",
      datePublished: today,
      dateModified: today,
      isPartOf: {
        "@type": "WebSite",
        name: "CivixThread",
        url: SITE_URL,
      },
      about: {
        "@type": "Thing",
        name: "United States Elections 2026",
      },
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/elections?address={address}`,
        "query-input": "required name=address",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Elections",
          item: `${SITE_URL}/elections`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: NATIONAL_FAQ.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "CivixThread",
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png`,
    },
  ];
};

const HeroBlock = ({ children }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
      color: "#ffffff",
      pt: { xs: 5, md: 7 },
      pb: { xs: 6, md: 8 },
    }}
  >
    <Container maxWidth="md">{children}</Container>
  </Box>
);

const SectionHeading = ({ children, id }) => (
  <Typography
    component="h2"
    id={id}
    sx={{
      fontSize: { xs: "1.4rem", md: "1.75rem" },
      fontWeight: 700,
      color: TEXT_PRIMARY,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const FaqItem = ({ q, a }) => (
  <Box
    component="article"
    sx={{
      mb: 2.5,
      pb: 2.5,
      borderBottom: "1px solid #e2e8f0",
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Typography
      component="h3"
      sx={{
        fontSize: { xs: "1rem", md: "1.05rem" },
        fontWeight: 700,
        color: TEXT_PRIMARY,
        mb: 0.75,
      }}
    >
      {q}
    </Typography>
    <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6 }}>{a}</Typography>
  </Box>
);

const CivicDashboardPromo = () => (
  <Box
    sx={{
      mt: 2.5,
      backgroundColor: "#ffffff",
      borderRadius: 3,
      p: { xs: 2.5, sm: 3 },
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
      color: TEXT_PRIMARY,
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 0, sm: 2.5 }}
      alignItems="center"
      justifyContent="space-between"
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: { xs: "0.98rem", sm: "1.05rem" },
            fontWeight: 600,
            color: TEXT_PRIMARY,
            lineHeight: 1.45,
            letterSpacing: 0,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Want a civic dashboard to navigate US politics from your phone or
          the web?
        </Typography>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          alignSelf: "center",
          width: { xs: "100%", sm: "auto" },
          mt: { xs: 1.5, sm: 0 },
        }}
      >
        <Button
          component={RouterLink}
          to="/auth/signup"
          variant="outlined"
          sx={{
            width: { xs: "100%", sm: "auto" },
            textTransform: "none",
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.02rem" },
            borderRadius: 2,
            minHeight: 48,
            px: 3,
            backgroundColor: CTA_GOLD_BG,
            color: CTA_GOLD_TEXT,
            border: `2px solid ${CTA_GOLD}`,
            boxShadow: "none",
            whiteSpace: { xs: "normal", sm: "nowrap" },
            "&:hover": {
              backgroundColor: CTA_GOLD_BG_HOVER,
              borderColor: CTA_GOLD,
              color: CTA_GOLD_TEXT,
              boxShadow: "none",
            },
            "&.Mui-focusVisible": {
              outline: `3px solid ${CTA_GOLD}`,
              outlineOffset: 2,
            },
          }}
        >
          Create your free account
        </Button>
      </Box>
    </Stack>
  </Box>
);

export default function PublicElectionsPage() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const resultsRef = useRef(null);
  const shouldScrollRef = useRef(false);

  const handleLookup = useCallback(async (params) => {
    setLoading(true);
    setSubmitError(null);
    shouldScrollRef.current = true;
    try {
      const data = await fetchPublicDWElections(params);
      if (data.error) {
        setSubmitError(
          "We couldn't load election data right now. Please try again in a moment."
        );
      }
      setResults(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!shouldScrollRef.current) return;
    if (loading) return;
    if (!results && !submitError) return;
    shouldScrollRef.current = false;
    requestAnimationFrame(() => {
      const target = resultsRef.current;
      if (!target) return;
      const headerOffset = 16;
      const top =
        target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
      window.scrollTo({ top, behavior: "smooth" });
      target.setAttribute("tabindex", "-1");
      target.focus({ preventScroll: true });
    });
  }, [loading, results, submitError]);

  const jsonLd = buildJsonLd();

  return (
    <Box component="main" sx={{ backgroundColor: "#ffffff", minHeight: "100vh" }}>
      <Helmet>
        <title>
          2026 Elections & Ballot Measures by State: Find Your Ballot |
          CivixThread
        </title>
        <meta
          name="description"
          content="Find 2026 elections by state or address. See voter registration deadlines, ballot measures, candidates, and voting guidance. Free."
        />
        <link rel="canonical" href={`${SITE_URL}/elections`} />
        <meta
          property="og:title"
          content="2026 Elections & Ballot Measures by State | CivixThread"
        />
        <meta
          property="og:description"
          content="Find 2026 elections by state or address. See voter registration deadlines, ballot measures, candidates, and voting guidance."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/elections`} />
        <meta property="og:image" content={`${SITE_URL}/og-elections-2026.png`} />
        <meta property="og:site_name" content="CivixThread" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="2026 Elections & Ballot Measures by State | CivixThread"
        />
        <meta
          name="twitter:description"
          content="Find 2026 elections by state or address. See voter registration deadlines, ballot measures, candidates, and voting guidance."
        />
        <meta
          name="twitter:image"
          content={`${SITE_URL}/og-elections-2026.png`}
        />
        <meta name="theme-color" content={PRIMARY} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <HeroBlock>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "1.45rem", sm: "1.7rem", md: "2rem" },
            fontWeight: 800,
            lineHeight: 1.2,
            mb: 1.25,
            textAlign: "center",
          }}
        >
          2026 Elections &amp; Ballot Measures by State
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "0.95rem", md: "1rem" },
            color: "rgba(255,255,255,0.95)",
            lineHeight: 1.55,
            mb: 3,
            maxWidth: 640,
            mx: "auto",
            textAlign: "center",
          }}
        >
          Find your 2026 elections, ballot measures, candidates, and
          registration deadlines. Enter your address for results specific to
          you. Free, no signup required.
        </Typography>

        <PublicElectionAddressForm
          onSubmit={handleLookup}
          loading={loading}
        />

        <CivicDashboardPromo />
      </HeroBlock>

      <Container
        maxWidth="md"
        sx={{ pt: { xs: 2, md: 2.5 }, pb: { xs: 4, md: 6 } }}
      >
        <Box
          ref={resultsRef}
          aria-label="Your upcoming elections"
          sx={{ outline: "none", scrollMarginTop: "16px" }}
        />
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {results && (
          <Box component="section" sx={{ mb: 4 }}>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: "1.3rem", md: "1.6rem" },
                fontWeight: 700,
                color: TEXT_PRIMARY,
                textAlign: "center",
                mb: 2,
              }}
            >
              Your upcoming elections
            </Typography>
            {results.precision === "state" || results.precision === "zip" ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                These are broad results. Enter a full address to see your
                exact ballot, local contests, and ballot measures.
              </Alert>
            ) : null}
            {results.elections.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                  No upcoming elections found for this lookup.
                </Typography>
                <Typography sx={{ color: TEXT_SECONDARY, mt: 1 }}>
                  Try a different address or browse your state below for the
                  full voter guide.
                </Typography>
              </Box>
            ) : (
              <DemocracyWorksElections
                elections={results.elections}
                hideReportFeedback
                showBallotData
              />
            )}
          </Box>
        )}

        <Box component="section" sx={{ mb: 5, textAlign: "center" }}>
          <Typography
            sx={{
              color: TEXT_SECONDARY,
              fontSize: "0.95rem",
              fontWeight: 600,
              mb: 2,
            }}
          >
            Stay engaged beyond election day.
          </Typography>
          <Button
            component={RouterLink}
            to="/features"
            variant="contained"
            endIcon={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              backgroundColor: PRIMARY,
              color: "#ffffff",
              minHeight: 44,
              px: 3.5,
              fontSize: "1.05rem",
              boxShadow: "0 4px 12px rgba(4, 120, 87, 0.25)",
              "&:hover": {
                backgroundColor: PRIMARY_DARK,
                boxShadow: "0 6px 16px rgba(4, 120, 87, 0.32)",
              },
            }}
          >
            Explore CivixThread Features
          </Button>
        </Box>

        {/* State directory */}
        <Box component="section" sx={{ mb: 5 }}>
          <SectionHeading id="state-directory">
            Browse 2026 election guides by state
          </SectionHeading>
          <Typography sx={{ color: TEXT_SECONDARY, mb: 2.5 }}>
            Each state guide covers voter registration, ID requirements, mail
            and early voting, ballot measures, and your state's election
            office contact info.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 1.25,
            }}
          >
            {US_STATES.map((s) => (
              <Button
                key={s.code}
                component={RouterLink}
                to={`/elections/${s.slug}`}
                sx={{
                  textTransform: "none",
                  justifyContent: "flex-start",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: TEXT_PRIMARY,
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  px: 1.5,
                  minHeight: 44,
                  "&:hover": {
                    backgroundColor: "#f0fdf4",
                    borderColor: PRIMARY,
                    color: PRIMARY_DARK,
                  },
                }}
              >
                {s.name}
              </Button>
            ))}
          </Box>
        </Box>

        {/* National FAQ */}
        <Box component="section" sx={{ mb: 5 }}>
          <SectionHeading>Common questions about U.S. elections</SectionHeading>
          {NATIONAL_FAQ.map((item) => (
            <FaqItem key={item.q} q={item.q} a={item.a} />
          ))}
        </Box>

        {/* Footer CTA + last updated */}
        <Box
          component="section"
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.2rem", md: "1.4rem" },
              fontWeight: 800,
              color: TEXT_PRIMARY,
              mb: 1,
            }}
          >
            Stay civically engaged year-round.
          </Typography>
          <Typography sx={{ color: TEXT_SECONDARY, mb: 2.5 }}>
            Free CivixThread account. No paywall. Built for real voters.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
          >
            <Button
              component={RouterLink}
              to="/auth/signup"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                backgroundColor: PRIMARY,
                color: "#ffffff",
                minHeight: 44,
                boxShadow: "none",
                "&:hover": { backgroundColor: PRIMARY_DARK, boxShadow: "none" },
              }}
            >
              Create your free account
            </Button>
            <Button
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                borderColor: PRIMARY,
                color: PRIMARY_DARK,
                minHeight: 44,
                "&:hover": {
                  borderColor: PRIMARY_DARK,
                  backgroundColor: "#f0fdf4",
                },
              }}
            >
              Download iOS app
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
